/**
 * South Carolina State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual S.C. Code Regs. R.61-77
 * 
 * Regulatory Body: South Carolina Department of Public Health (DPH) - formerly DHEC
 * Primary Regulation: S.C. Code Regs. R.61-77 - Standards for Licensing Home Health Agencies
 * Enabling Statute: S.C. Code §§ 44-69-10 et seq. (Licensure of Home Health Agencies)
 * 
 * CRITICAL CORRECTION: Original file cited fabricated section numbers throughout.
 * 
 * VERIFIED R.61-77 STRUCTURE (Adopted 5/27/2022):
 * - Section 100: Definitions, Licensure, and Certification (101-102)
 * - Section 200: Enforcement of Regulations (201-203)
 * - Section 300: Enforcement Actions (301-302)
 * - Section 400: Policies and Procedures
 * - Section 500: Staffing (501 General, 502 Administrator, 503 Clinical Manager, 504 Health Status)
 * - Section 600: Reporting (601 Incidents, 602 Administrator Changes, 603 Agency Closure, 604 Joint Annual Report)
 * - Section 700: Patient Records (701 Content, 702 Record Maintenance, 703 Authentication, 704 Record Retention)
 * - Section 800: Admissions, Discharges, and Transfers (801-803)
 * - Section 900: Patient Care, Treatment, and Services (901 General, 902 Comprehensive Assessment, 903 Treatment Plan)
 * - Section 1000: Patient Rights (1001 General, 1002 Informed Consent, 1003 Patient Protections)
 * - Section 1100: [Reserved]
 * - Section 1200: Medication and Treatment Orders
 * - Section 1300: Agreements with Contracted Parties
 * - Section 1400: Emergency Procedures and Disaster Preparedness (1401-1402)
 * - Section 1700: Infection Control (1701-1702)
 * - Section 1800: Quality Improvement Program
 * 
 * NOTE: As of 2024, DHEC split into DPH (public health) and DEHEC (environment).
 * Healthcare facility licensing is now under SC DPH.
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const SOUTH_CAROLINA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & CERTIFICATION
  // ============================================
  {
    id: 'SC-A1',
    itemId: 'A1',
    stateCode: 'SC',
    title: 'Home Health Agency License Status',
    description: 'South Carolina requires all home health agencies to be licensed under R.61-77',
    severity: 'high',
    regulationCode: 'Section 102',
    regulationName: 'License',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from SC DPH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R.61-77 Section 102, licenses must be renewed annually.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DPH license certificate',
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
      {
        id: 'q3',
        question: 'Is your license displayed in a conspicuous place?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // License not displayed per R.61-77 Section 102
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your SC DPH Home Health Agency license is current per R.61-77 Section 102.',
      atRisk: 'Your license expires within 90 days or is not displayed in a conspicuous place. Submit renewal immediately.',
      nonCompliant: 'Operating without valid license violates S.C. Code § 44-69-75. Contact SC DPH at (803) 545-4370.',
      unknown: 'Verify license status with SC DPH.',
    },
    stateNotes: [
      'Licenses must be renewed annually per R.61-77 Section 102',
      'License must be displayed in a conspicuous place',
      'Operating without a license is a misdemeanor per S.C. Code § 44-69-75',
      'Application fees and renewal fees apply per Section 102',
    ],
  },

  {
    id: 'SC-A2',
    itemId: 'A2',
    stateCode: 'SC',
    title: 'Administrator Qualifications',
    description: 'Administrator requirements per R.61-77 Section 502',
    severity: 'high',
    regulationCode: 'Section 502',
    regulationName: 'Administrator',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated Administrator per Section 502?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R.61-77 Section 101.B, Administrator is the individual designated by the governing body who is in charge of and responsible for agency administration.',
      },
      {
        id: 'q2',
        question: 'Does your Administrator have documented qualifications?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, documented' },
          { value: 'no', label: 'No / not documented' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets R.61-77 Section 502 requirements.',
      atRisk: 'Administrator qualifications should be documented.',
      nonCompliant: 'No designated Administrator violates R.61-77 Section 502.',
      unknown: 'Verify Administrator qualifications.',
    },
    stateNotes: [
      'Administrator is designated by governing body per Section 101.B',
      'Administrator must be accessible during normal operating hours',
      'Qualifications must be documented in personnel file',
      'Administrator changes require notification per Section 602',
    ],
  },

  {
    id: 'SC-A3',
    itemId: 'A3',
    stateCode: 'SC',
    title: 'Administrator Change Notification',
    description: 'Administrator changes must be reported per R.61-77 Section 602',
    severity: 'high',
    regulationCode: 'Section 602',
    regulationName: 'Admin Changes',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Has there been any Administrator change in the past year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Was the Department notified within 72 hours of the change in Administrator status per Section 602.A?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
      {
        id: 'q3',
        question: 'Was written notification submitted within 10 calendar days with new Administrator name, qualifications, and effective date per Section 602.B?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator change notification meets R.61-77 Section 602 requirements.',
      atRisk: 'Verify all notifications were submitted timely.',
      nonCompliant: 'Failure to notify DPH of Administrator changes violates R.61-77 Section 602.',
      unknown: 'Review Administrator change notification requirements.',
    },
    stateNotes: [
      'Notify DPH within 72 hours of any change in Administrator status (§ 602.A)',
      'Written notification within 10 calendar days with name, qualifications, and effective date (§ 602.B)',
    ],
  },

  {
    id: 'SC-A4',
    itemId: 'A4',
    stateCode: 'SC',
    title: 'Clinical Manager Requirements',
    description: 'Clinical Manager requirements per R.61-77 Section 503',
    severity: 'high',
    regulationCode: 'Section 503',
    regulationName: 'Clinical Manager',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated Clinical Manager per Section 503?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the Clinical Manager a Registered Nurse or licensed therapist?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical Manager meets R.61-77 Section 503 requirements.',
      atRisk: 'Clinical Manager qualifications may need verification.',
      nonCompliant: 'No Clinical Manager violates R.61-77 Section 503.',
      unknown: 'Verify Clinical Manager requirements.',
    },
    stateNotes: [
      'Clinical Manager must be RN or licensed therapist per Section 503',
      'Responsible for clinical oversight of patient care services',
      'Must maintain current South Carolina licensure',
      'Qualifications documented in personnel file',
    ],
  },

  {
    id: 'SC-A5',
    itemId: 'A5',
    stateCode: 'SC',
    title: 'Policies and Procedures',
    description: 'Written policies and procedures per R.61-77 Section 400',
    severity: 'medium',
    regulationCode: 'Section 400',
    regulationName: 'Policies',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written policies and procedures per Section 400?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are policies reviewed and updated as needed?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Policies and procedures meet R.61-77 Section 400 requirements.',
      atRisk: 'Policies should be reviewed and updated regularly.',
      nonCompliant: 'No written policies violates R.61-77 Section 400.',
      unknown: 'Review policy requirements.',
    },
    stateNotes: [
      'Written policies required per R.61-77 Section 400',
      'Policies must be reviewed and updated as needed',
      'Must be available to staff and surveyors upon request',
      'Policies govern all aspects of agency operations',
    ],
  },

  {
    id: 'SC-A6',
    itemId: 'A6',
    stateCode: 'SC',
    title: 'Branch Office Requirements',
    description: 'Branch office requirements per R.61-77 Section 101.D',
    severity: 'medium',
    regulationCode: 'Section 101.D',
    regulationName: 'Branch Office',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency operate any branch offices?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are branch offices sufficiently close to share administration, supervision, and services with the parent agency per Section 101.D?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Branch office requirements met per R.61-77.',
      atRisk: 'Verify branch office shares administration with parent agency.',
      nonCompliant: 'Branch offices not meeting proximity requirements may need separate licensure.',
      unknown: 'Review branch office requirements.',
    },
    stateNotes: [
      'Branch office must be sufficiently close to parent per Section 101.D',
      'Must share administration, supervision, and services with parent',
      'Branch offices too distant may require separate licensure',
      'Parent agency responsible for branch office compliance',
    ],
  },

  // ============================================
  // SECTION B: STAFFING & HEALTH STATUS
  // ============================================
  {
    id: 'SC-B1',
    itemId: 'B1',
    stateCode: 'SC',
    title: 'General Staffing Requirements',
    description: 'General staffing requirements per R.61-77 Section 501',
    severity: 'high',
    regulationCode: 'Section 501',
    regulationName: 'Staffing',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain sufficient qualified staff to meet patient needs per Section 501?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are all staff appropriately licensed/certified for their roles?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff' },
          { value: 'most', label: 'Most staff' },
          { value: 'no', label: 'No / Not verified' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Staffing meets R.61-77 Section 501 requirements.',
      atRisk: 'Some staff may need license/certification verification.',
      nonCompliant: 'Inadequate staffing violates R.61-77 Section 501.',
      unknown: 'Review staffing requirements.',
    },
    stateNotes: [
      'Sufficient qualified staff required per Section 501',
      'All staff must be licensed/certified for their roles',
      'Staff qualifications must be verified and documented',
      'SC Board of Nursing for license verification: llr.sc.gov/nurse/',
    ],
  },

  {
    id: 'SC-B2',
    itemId: 'B2',
    stateCode: 'SC',
    title: 'Staff Health Status and TB Screening',
    description: 'Health status requirements per R.61-77 Section 504 and TB per Section 1702',
    severity: 'high',
    regulationCode: 'Section 504',
    regulationName: 'Health Status',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff have documented health status per Section 504?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff' },
          { value: 'most', label: 'Most staff' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What TB testing method do you use per Section 1702.A?',
        inputType: 'select',
        options: [
          { value: 'tst', label: 'Tuberculin Skin Test (TST)' },
          { value: 'bamt', label: 'Blood Assay for Mycobacterium tuberculosis (BAMT/IGRA)' },
          { value: 'none', label: 'None' },
        ],
        required: true,
        helpText: 'Per Section 1702.A, agency shall utilize either TST or BAMT for detecting TB infection.',
      },
      {
        id: 'q3',
        question: 'Do you conduct an annual TB risk assessment per CDC guidelines per Section 1702.B?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q4',
        question: 'Do you determine baseline TB status for all staff per CDC and Department guidelines per Section 1702.C?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['tst', 'bamt'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Staff health status and TB screening meet R.61-77 Sections 504 and 1702.',
      atRisk: 'Some staff may be missing health/TB documentation.',
      nonCompliant: 'Missing health status or TB screening violates R.61-77 Sections 504/1702.',
      unknown: 'Review health status and TB requirements.',
    },
    stateNotes: [
      'Use TST or BAMT for TB detection (§ 1702.A)',
      'Annual TB risk assessment per CDC guidelines (§ 1702.B)',
      'Baseline TB status for all staff per CDC/Department guidelines (§ 1702.C)',
    ],
  },

  {
    id: 'SC-B3',
    itemId: 'B3',
    stateCode: 'SC',
    title: 'Professional License Verification',
    description: 'Verified credentials for licensed personnel',
    severity: 'high',
    regulationCode: 'Section 501',
    regulationName: 'Staff Qualifications',
    agencyName: 'South Carolina Board of Nursing',
    agencyAbbr: 'SC BON',
    agencyPhone: '(803) 896-4550',
    agencyUrl: 'https://llr.sc.gov/nurse/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses with SC licensing boards before patient contact?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track and verify license expirations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'License verification meets requirements.',
      atRisk: 'License verification may be inconsistent.',
      nonCompliant: 'No license verification process in place.',
      unknown: 'Verify all staff licenses with SC licensing boards.',
    },
    stateNotes: [
      'SC Board of Nursing: llr.sc.gov/nurse/ - (803) 896-4550',
      'Verify all professional licenses before patient contact',
      'Track license expiration dates for timely renewal',
      'Primary source verification recommended',
    ],
  },

  // ============================================
  // SECTION C: INCIDENT REPORTING
  // ============================================
  {
    id: 'SC-C1',
    itemId: 'C1',
    stateCode: 'SC',
    title: 'Incident Reporting',
    description: 'Incident reporting requirements per R.61-77 Section 601',
    severity: 'high',
    regulationCode: 'Section 601',
    regulationName: 'Incidents',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you report required incidents via the Department\'s electronic reporting system per Section 601?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do incident reports include all required elements (agency name, license number, incident type, date, patient ID, age/sex, staff affected, witnesses, cause, investigation results, description, treatment)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all elements' },
          { value: 'most', label: 'Most elements' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting meets R.61-77 Section 601 requirements.',
      atRisk: 'Incident reports may be missing required elements.',
      nonCompliant: 'Failure to report incidents via electronic system violates R.61-77 Section 601.',
      unknown: 'Review incident reporting requirements.',
    },
    stateNotes: [
      'Use Department electronic reporting system per Section 601',
      'Reports must include: agency name, license number, incident type, date, patient ID',
      'Include staff affected, witnesses, cause, investigation results',
      'SC Adult Protective Services hotline: 1-888-227-3487',
    ],
  },

  {
    id: 'SC-C2',
    itemId: 'C2',
    stateCode: 'SC',
    title: 'Joint Annual Report',
    description: 'Annual report submission per R.61-77 Section 604',
    severity: 'medium',
    regulationCode: 'Section 604',
    regulationName: 'Annual Report',
    agencyName: 'South Carolina Revenue and Fiscal Affairs Office',
    agencyAbbr: 'RFA',
    agencyPhone: '(803) 734-3793',
    agencyUrl: 'https://rfa.sc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you submit the Joint Annual Report as specified by the Department per Section 604?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R.61-77 Section 101.T, this is an annual statistical and utilization data report submitted to SC RFA.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Joint Annual Report submitted per R.61-77 Section 604.',
      atRisk: 'Verify annual report submission.',
      nonCompliant: 'Failure to submit Joint Annual Report violates R.61-77 Section 604.',
      unknown: 'Review Joint Annual Report requirements.',
    },
    stateNotes: [
      'Annual statistical and utilization data report per Section 101.T',
      'Submitted to SC Revenue and Fiscal Affairs Office',
      'Required for all licensed home health agencies',
      'Failure to submit may affect license renewal',
    ],
  },

  // ============================================
  // SECTION D: PATIENT RECORDS
  // ============================================
  {
    id: 'SC-D1',
    itemId: 'D1',
    stateCode: 'SC',
    title: 'Patient Record Content',
    description: 'Patient record content requirements per R.61-77 Section 701',
    severity: 'high',
    regulationCode: 'Section 701',
    regulationName: 'Records Content',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain an organized record for each patient per Section 701.A?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are all entries permanently written, typed, or electronic media, authenticated by author, and dated per Section 701.A?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do patient records reflect services, treatment, and care provided per Section 701.B?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient records meet R.61-77 Section 701 requirements.',
      atRisk: 'Patient record documentation may need improvement.',
      nonCompliant: 'Patient records do not meet R.61-77 Section 701 requirements.',
      unknown: 'Review patient record requirements.',
    },
    stateNotes: [
      'Organized record required for each patient per Section 701.A',
      'Entries must be permanent (written, typed, or electronic)',
      'All entries must be authenticated by author and dated',
      'Records must reflect services, treatment, and care provided',
    ],
  },

  {
    id: 'SC-D2',
    itemId: 'D2',
    stateCode: 'SC',
    title: 'Record Authentication',
    description: 'Record authentication requirements per R.61-77 Section 703',
    severity: 'medium',
    regulationCode: 'Section 703',
    regulationName: 'Authentication',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Are all patient record entries properly authenticated per Section 703?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all entries' },
          { value: 'most', label: 'Most entries' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record authentication meets R.61-77 Section 703.',
      atRisk: 'Some entries may not be properly authenticated.',
      nonCompliant: 'Record authentication not meeting requirements.',
      unknown: 'Review authentication requirements.',
    },
    stateNotes: [
      'All entries must be authenticated per Section 703',
      'Authentication includes signature and credentials',
      'Electronic signatures acceptable per SC law',
      'Authentication must be timely and verifiable',
    ],
  },

  {
    id: 'SC-D3',
    itemId: 'D3',
    stateCode: 'SC',
    title: 'Record Retention',
    description: 'Record retention requirements per R.61-77 Section 704',
    severity: 'high',
    regulationCode: 'Section 704',
    regulationName: 'Record Retention',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient records per Section 704?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no policy' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets R.61-77 Section 704 requirements.',
      atRisk: 'Verify retention period meets requirements.',
      nonCompliant: 'Record retention policy does not meet requirements.',
      unknown: 'Review record retention requirements per Section 704.',
    },
    stateNotes: [
      'Minimum 5-year retention per Section 704',
      'Records for minors retained longer per state law',
      'Proper destruction methods required when disposing',
      'If agency closes, provisions for record custody required',
    ],
  },

  // ============================================
  // SECTION E: PATIENT CARE & TREATMENT
  // ============================================
  {
    id: 'SC-E1',
    itemId: 'E1',
    stateCode: 'SC',
    title: 'Comprehensive Assessment',
    description: 'Comprehensive assessment requirements per R.61-77 Section 902',
    severity: 'high',
    regulationCode: 'Section 902',
    regulationName: 'Assessment',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete comprehensive assessments for all patients per Section 902?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients' },
          { value: 'most', label: 'Most patients' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Section 101.E, Comprehensive Assessment reflects current health status and progress toward goals.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Comprehensive assessments meet R.61-77 Section 902.',
      atRisk: 'Some patients may be missing comprehensive assessments.',
      nonCompliant: 'Comprehensive assessments not being completed.',
      unknown: 'Review assessment requirements.',
    },
    stateNotes: [
      'Comprehensive Assessment reflects current health status per Section 101.E',
      'Must reflect progress toward goals',
      'Completed for all patients per Section 902',
      'Forms basis for treatment planning',
    ],
  },

  {
    id: 'SC-E2',
    itemId: 'E2',
    stateCode: 'SC',
    title: 'Treatment Plan',
    description: 'Treatment plan requirements per R.61-77 Section 903',
    severity: 'high',
    regulationCode: 'Section 903',
    regulationName: 'Treatment Plan',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written treatment plan for EVERY patient before services per Section 903?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are treatment plans developed in consultation with physicians or authorized healthcare providers?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Treatment plans meet R.61-77 Section 903 requirements.',
      atRisk: 'Treatment plans may not be in place for all patients.',
      nonCompliant: 'Services without treatment plans violates R.61-77 Section 903.',
      unknown: 'Review treatment plan requirements.',
    },
    stateNotes: [
      'Written treatment plan required before services per Section 903',
      'Developed in consultation with physician or authorized provider',
      'Must include specific services, frequency, and goals',
      'Reviewed and updated as patient condition changes',
    ],
  },

  {
    id: 'SC-E3',
    itemId: 'E3',
    stateCode: 'SC',
    title: 'Admission Requirements',
    description: 'Admission requirements per R.61-77 Section 801',
    severity: 'high',
    regulationCode: 'Section 801',
    regulationName: 'Admission',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Are admission criteria documented and followed per Section 801?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is documentation of patient rights explanation included with admission per Section 801.C (referencing Section 1001)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Admission process meets R.61-77 Section 801 requirements.',
      atRisk: 'Patient rights documentation may not be included at admission.',
      nonCompliant: 'Admission process does not meet requirements.',
      unknown: 'Review admission requirements.',
    },
    stateNotes: [
      'Admission criteria must be documented and followed per Section 801',
      'Patient rights explanation required per Section 801.C',
      'References patient rights in Section 1001',
      'Documentation of rights explanation must be in patient record',
    ],
  },

  {
    id: 'SC-E4',
    itemId: 'E4',
    stateCode: 'SC',
    title: 'Discharge Requirements',
    description: 'Discharge requirements per R.61-77 Section 802',
    severity: 'medium',
    regulationCode: 'Section 802',
    regulationName: 'Discharge',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you follow documented discharge procedures per Section 802?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are discharge summaries completed for all patients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients' },
          { value: 'most', label: 'Most patients' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Discharge process meets R.61-77 Section 802.',
      atRisk: 'Some patients may not have discharge summaries.',
      nonCompliant: 'Discharge process does not meet requirements.',
      unknown: 'Review discharge requirements.',
    },
    stateNotes: [
      'Documented discharge procedures required per Section 802',
      'Discharge summaries must be completed for all patients',
      'Includes reason for discharge and patient status',
      'Referrals and follow-up care must be documented',
    ],
  },

  // ============================================
  // SECTION F: PATIENT RIGHTS
  // ============================================
  {
    id: 'SC-F1',
    itemId: 'F1',
    stateCode: 'SC',
    title: 'Patient Rights - General',
    description: 'Patient rights requirements per R.61-77 Section 1001',
    severity: 'high',
    regulationCode: 'Section 1001',
    regulationName: 'Patient Rights',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL patients with written notice of their rights per Section 1001?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all at admission' },
          { value: 'most', label: 'Most patients' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document patient acknowledgment of rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you have a grievance procedure and inform patients of it?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights meet R.61-77 Section 1001 requirements.',
      atRisk: 'Some patients may not receive rights notice.',
      nonCompliant: 'Patients not receiving written rights violates R.61-77 Section 1001.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Written notice of rights required at admission per Section 1001',
      'Patient acknowledgment must be documented',
      'Grievance procedure must be provided',
      'SC DPH complaint line: (803) 545-4370',
    ],
  },

  {
    id: 'SC-F2',
    itemId: 'F2',
    stateCode: 'SC',
    title: 'Informed Consent',
    description: 'Informed consent requirements per R.61-77 Section 1002',
    severity: 'high',
    regulationCode: 'Section 1002',
    regulationName: 'Informed Consent',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you obtain informed consent from patients per Section 1002?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients' },
          { value: 'most', label: 'Most patients' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Informed consent meets R.61-77 Section 1002.',
      atRisk: 'Informed consent may not be obtained from all patients.',
      nonCompliant: 'Failure to obtain informed consent violates R.61-77 Section 1002.',
      unknown: 'Review informed consent requirements.',
    },
    stateNotes: [
      'Informed consent required per Section 1002',
      'Patient must understand treatment plan and alternatives',
      'Consent must be documented in patient record',
      'Patient may refuse treatment with informed consequences',
    ],
  },

  {
    id: 'SC-F3',
    itemId: 'F3',
    stateCode: 'SC',
    title: 'Patient Protections',
    description: 'Patient protection requirements per R.61-77 Section 1003',
    severity: 'high',
    regulationCode: 'Section 1003',
    regulationName: 'Protections',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you comply with all federal, state, and local patient protection laws including Title VI, Civil Rights Act, and ADA per Section 1003?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you ensure no discrimination based on source of payment per Section 1003?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient protections meet R.61-77 Section 1003.',
      atRisk: 'Patient protection policies may need review.',
      nonCompliant: 'Patient protection requirements not met.',
      unknown: 'Review patient protection requirements.',
    },
    stateNotes: [
      'Comply with Title VI, Civil Rights Act, and ADA per Section 1003',
      'No discrimination based on source of payment',
      'All federal, state, and local patient protection laws apply',
      'Equal treatment regardless of race, color, religion, sex, or national origin',
    ],
  },

  // ============================================
  // SECTION G: INFECTION CONTROL
  // ============================================
  {
    id: 'SC-G1',
    itemId: 'G1',
    stateCode: 'SC',
    title: 'Infection Control Program',
    description: 'Infection control requirements per R.61-77 Section 1701',
    severity: 'high',
    regulationCode: 'Section 1701',
    regulationName: 'Infection Control',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include required elements per Section 1701?',
        inputType: 'checklist',
        options: [
          { value: 'standard', label: 'Standard precautions' },
          { value: 'transmission', label: 'Transmission-based precautions' },
          { value: 'contact', label: 'Contact precautions' },
          { value: 'airborne', label: 'Airborne precautions' },
          { value: 'isolation', label: 'Isolation techniques' },
          { value: 'hand_hygiene', label: 'Hand hygiene per Section 1701.B' },
          { value: 'ppe', label: 'Barrier precautions/gloves per Section 1701.B' },
          { value: 'waste', label: 'Proper disposal of toxic/hazardous substances' },
        ],
        required: true,
        helpText: 'Per Section 1701.A, must comply with OSHA Bloodborne Pathogens Standard, CDC guidelines, and R.61-105.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control meets R.61-77 Section 1701.',
      atRisk: 'Infection control program may be incomplete.',
      nonCompliant: 'Infection control missing critical elements per Section 1701.',
      unknown: 'Review infection control against R.61-77 Section 1701.',
    },
    stateNotes: [
      'Must comply with OSHA Bloodborne Pathogens Standard per Section 1701.A',
      'Follow CDC guidelines per Section 1701.A',
      'Comply with R.61-105 (SC Infectious Waste Regulations)',
      'Hand hygiene per Section 1701.B required',
    ],
  },

  // ============================================
  // SECTION H: EMERGENCY PREPAREDNESS
  // ============================================
  {
    id: 'SC-H1',
    itemId: 'H1',
    stateCode: 'SC',
    title: 'Disaster Preparedness',
    description: 'Emergency preparedness requirements per R.61-77 Section 1401',
    severity: 'high',
    regulationCode: 'Section 1401',
    regulationName: 'Emergency Plan',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a disaster preparedness plan per Section 1401?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your plan address SC-specific hazards (hurricanes, flooding)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Disaster preparedness meets R.61-77 Section 1401.',
      atRisk: 'Disaster plan may not address SC-specific hazards.',
      nonCompliant: 'No disaster preparedness plan violates R.61-77 Section 1401.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Written disaster preparedness plan required per Section 1401',
      'Must address SC-specific hazards: hurricanes, flooding',
      'Annual review and update recommended',
      'SC Emergency Management Division: scemd.org',
    ],
  },

  {
    id: 'SC-H2',
    itemId: 'H2',
    stateCode: 'SC',
    title: 'Continuity of Care',
    description: 'Continuity of care during emergencies per R.61-77 Section 1402',
    severity: 'high',
    regulationCode: 'Section 1402',
    regulationName: 'Continuity of Care',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a plan for continuity of care during emergencies per Section 1402?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Continuity of care plan meets R.61-77 Section 1402.',
      atRisk: 'Verify continuity of care procedures.',
      nonCompliant: 'No continuity of care plan violates R.61-77 Section 1402.',
      unknown: 'Review continuity requirements.',
    },
    stateNotes: [
      'Continuity of care during emergencies required per Section 1402',
      'Patient priority lists for emergency contact essential',
      'Coordination with local emergency services',
      'Staff notification and communication procedures',
    ],
  },

  // ============================================
  // SECTION I: QUALITY IMPROVEMENT
  // ============================================
  {
    id: 'SC-I1',
    itemId: 'I1',
    stateCode: 'SC',
    title: 'Quality Improvement Program',
    description: 'Quality improvement requirements per R.61-77 Section 1800',
    severity: 'medium',
    regulationCode: 'Section 1800',
    regulationName: 'Quality Improvement',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a quality improvement program per Section 1800?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your QI program include ongoing monitoring and performance improvement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets R.61-77 Section 1800.',
      atRisk: 'QI program may need enhancement.',
      nonCompliant: 'No QI program violates R.61-77 Section 1800.',
      unknown: 'Review QI requirements.',
    },
    stateNotes: [
      'Ongoing QI program required per Section 1800',
      'Must include monitoring and performance improvement',
      'Data collection on quality indicators',
      'Action plans for identified deficiencies',
    ],
  },

  // ============================================
  // SECTION J: MEDICATION ORDERS & CONTRACTS
  // ============================================
  {
    id: 'SC-J1',
    itemId: 'J1',
    stateCode: 'SC',
    title: 'Medication and Treatment Orders',
    description: 'Medication order requirements per R.61-77 Section 1200',
    severity: 'high',
    regulationCode: 'Section 1200',
    regulationName: 'Med Orders',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Are verbal orders received only by licensed nurses or licensed therapists per Section 1200?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do verbal orders include required elements (time, description, provider ID, receiver ID)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are verbal orders authenticated and dated by physician or authorized healthcare provider?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Medication and treatment orders meet R.61-77 Section 1200.',
      atRisk: 'Verbal order procedures may need improvement.',
      nonCompliant: 'Verbal orders not being handled by licensed personnel.',
      unknown: 'Review medication order requirements.',
    },
    stateNotes: [
      'Verbal orders received only by licensed nurses or therapists per Section 1200',
      'Must include time, description, provider ID, receiver ID',
      'Authentication and dating by physician required',
      'Written confirmation within required timeframe',
    ],
  },

  {
    id: 'SC-J2',
    itemId: 'J2',
    stateCode: 'SC',
    title: 'Agreements with Contracted Parties',
    description: 'Contract requirements per R.61-77 Section 1300',
    severity: 'medium',
    regulationCode: 'Section 1300',
    regulationName: 'Contracts',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facility-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written agreements with all contracted parties per Section 1300?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all contractors' },
          { value: 'most', label: 'Most contractors' },
          { value: 'no', label: 'No / No contractors' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contractor agreements meet R.61-77 Section 1300.',
      atRisk: 'Some contractor agreements may be missing.',
      nonCompliant: 'Missing contractor agreements violates Section 1300.',
      unknown: 'Review contractor agreement requirements.',
    },
    stateNotes: [
      'Written agreements required with all contracted parties per Section 1300',
      'Agreements must specify services and responsibilities',
      'Contractor qualifications must be verified',
      'Agency remains responsible for contracted services',
    ],
  },

  // ============================================
  // SECTION K: BACKGROUND CHECKS & OIG
  // ============================================
  {
    id: 'SC-K1',
    itemId: 'K1',
    stateCode: 'SC',
    title: 'SLED Background Check',
    description: 'Criminal background check requirements',
    severity: 'high',
    regulationCode: '44-7-2910',
    regulationName: 'Background Check',
    agencyName: 'South Carolina Law Enforcement Division',
    agencyAbbr: 'SLED',
    agencyPhone: '(803) 896-1443',
    agencyUrl: 'https://www.sled.sc.gov/backgroundchecks.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct SLED criminal background checks on all direct care staff before patient contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff before contact' },
          { value: 'most', label: 'Most staff' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff ever start before background checks are complete?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never' },
          { value: 'yes', label: 'Yes - sometimes' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'SLED background checks meet requirements.',
      atRisk: 'Background check process may have gaps.',
      nonCompliant: 'Staff working without background checks violates state law.',
      unknown: 'Verify background check procedures.',
    },
    stateNotes: [
      'SLED criminal background check required per S.C. Code 44-7-2910',
      'Background check required before patient contact',
      'SLED: www.sled.sc.gov/backgroundchecks.html - (803) 896-1443',
      'FBI fingerprint check recommended for comprehensive screening',
    ],
  },

  {
    id: 'SC-K2',
    itemId: 'K2',
    stateCode: 'SC',
    title: 'OIG Exclusion List Verification',
    description: 'Federal OIG exclusion list requirement',
    severity: 'high',
    regulationCode: '42 USC 1320a-7',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check OIG exclusion lists for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Do not check' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'quarterly', result: 'AT_RISK' },
      { condition: 'includes', value: ['hire_only', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion verification meets federal requirements.',
      atRisk: 'OIG verification should be monthly per OIG guidance.',
      nonCompliant: 'OIG exclusion verification required. Billing for excluded employees is fraud.',
      unknown: 'Review OIG exclusion requirements.',
    },
    stateNotes: [
      'OIG LEIE check required per 42 USC 1320a-7',
      'Check exclusions.oig.hhs.gov before hire and monthly',
      'SAM.gov check also recommended for federal debarment',
      'Employing excluded individual results in civil monetary penalties',
    ],
  },

  {
    id: 'SC-K3',
    itemId: 'K3',
    stateCode: 'SC',
    title: 'Nurse Aide Abuse Registry Check',
    description: 'SC Nurse Aide Abuse Registry verification',
    severity: 'high',
    regulationCode: '44-7-264',
    regulationName: 'Abuse Registry',
    agencyName: 'South Carolina Department of Public Health',
    agencyAbbr: 'SC DPH',
    agencyPhone: '(803) 545-4370',
    agencyUrl: 'https://dph.sc.gov/health-regulation/healthcare-facilities-services/nurse-aide',
    questions: [
      {
        id: 'q1',
        question: 'Do you check SC Nurse Aide Abuse Registry before hiring aides?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document the registry check?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Abuse Registry checks conducted and documented.',
      atRisk: 'Registry checks may be inconsistent.',
      nonCompliant: 'Failure to check Abuse Registry violates state requirements.',
      unknown: 'Verify Abuse Registry procedures.',
    },
    stateNotes: [
      'SC Nurse Aide Abuse Registry check required per S.C. Code 44-7-264',
      'Check registry before hiring aides',
      'SC DPH Nurse Aide Registry: dph.sc.gov/health-regulation/healthcare-facilities-services/nurse-aide',
      'Document registry check in employee file',
    ],
  },
];

export default SOUTH_CAROLINA_QUESTIONS;